import { useCallback } from 'react';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import { useParams } from 'react-router-dom';
import useMedicalFileUpload from './useMedicalFileUpload';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import useMedicalButton from './useMedicalButton';
import { buildMedicalProviderPaths } from '../utils/caseFieldPaths';
import { validationFilesFunc, generateDefaultProviderName } from '../utils/helper';
import { UPDATE_CASE_ACTION } from '@demandproai/shared';

const useNewMedicalProvider = ({ providerType }) => {
    const { formik, selectedTiffIndex, setFileErrorList } = useAddCaseFormContext();
    const { caseId } = useParams();

    const formikValues = formik.values;
    const setFieldValue = formik.setFieldValue;

    const { uploadMedicalRecords, isLoading } = useMedicalFileUpload({ providerType });
    const { updateCase } = useCaseFieldUpdate({ caseId });

    const getIsDuplicateProviderName = useCallback((name) => {
        return formikValues.clients?.[selectedTiffIndex]?.medicalProviders?.[providerType]?.medicalRecords?.find(obj => obj.providerName.trim().toLowerCase() === name.trim().toLowerCase());
    }, [selectedTiffIndex, providerType, formikValues]);

    const uploadMedicalFiles = useCallback(async (files) => {
        const validFiles = await validationFilesFunc(files, setFileErrorList);
        const existingRecords = formikValues?.clients?.[selectedTiffIndex]?.medicalProviders?.[providerType]?.medicalRecords || [];
        const newProviderIndex = existingRecords.length;

        const rawProviderName = formikValues.clients?.[selectedTiffIndex]?.medicalProviders?.[providerType]?.providerName?.trim();
        const clientFullName = formikValues.clients?.[selectedTiffIndex]?.personalInfo?.clientName;
        const providerName = rawProviderName || generateDefaultProviderName(clientFullName, newProviderIndex);

        const medicalproviderPaths = buildMedicalProviderPaths({ selectedTiffIndex, providerType });

        await updateCase({ data: { [medicalproviderPaths.records]: { providerName, source: [] } }, action: UPDATE_CASE_ACTION.ADD });

        const newEntry = { providerName, source: validFiles };
        const updatedEntry = [...existingRecords, newEntry];

        const medicalRecordPath = `clients[${selectedTiffIndex}].medicalProviders.${providerType}.medicalRecords`;
        setFieldValue(`clients[${selectedTiffIndex}].medicalProviders.${providerType}.medicalRecords`, []);
        setFieldValue(`clients[${selectedTiffIndex}].medicalProviders.${providerType}.providerName`, '');
        setFieldValue(medicalRecordPath, updatedEntry);

        const dedicatedFiles = await uploadMedicalRecords(validFiles, {
            fieldKey: medicalproviderPaths.source(newProviderIndex),
            action: 'add'
        });
        newEntry.source = dedicatedFiles;
        setFieldValue(medicalRecordPath, updatedEntry);
    }, [formikValues, selectedTiffIndex, providerType, setFieldValue, setFileErrorList, updateCase, uploadMedicalRecords]);

    const addMedicalFiles = async (e) => {
        const files = Array.from(e.currentTarget.files);
        await uploadMedicalFiles(files);
    };

    const { buttonDisplayText, isButonDisabled, checkProviderName } = useMedicalButton(true, getIsDuplicateProviderName);

    const fieldName = `clients[${selectedTiffIndex}].medicalProviders.${providerType}.providerName`;
    const providerName = formikValues?.clients?.[selectedTiffIndex]?.medicalProviders?.[providerType]?.providerName || '';

    const handleProviderNameChange = (e) => {
        const newValue = e.target.value;
        setFieldValue(fieldName, newValue);
        checkProviderName(newValue);
    };

    return {
        fieldName,
        providerName,
        handleProviderNameChange,
        buttonDisplayText,
        isButonDisabled,
        isLoading,
        addMedicalFiles,
        addMedicalFilesFromArray: uploadMedicalFiles,
        providerType
    };
};

export default useNewMedicalProvider;
